/**
 * Workflow payloads — swaps, substitutions, the approval trail and notifications — mirrored from
 * the API. Nullable fields arrive absent rather than null (the API writes with WhenWritingNull), so
 * test them for truthiness.
 */

import type { AssignmentStatus, ConflictSeverity, OnCallTier, RosterStatus } from "@/features/roster/types";

export type SwapKind = "Exchange" | "GiveAway";

export type SwapStatus =
  | "Requested" | "AcceptedByPeer" | "DeclinedByPeer" | "Approved" | "Rejected" | "Cancelled";

export type SwapAction = "accept" | "decline" | "approve" | "reject" | "cancel";

export interface WorkflowDuty {
  assignmentId: string;
  rosterId: string;
  rosterName: string;
  employeeId: string;
  employeeName: string;
  dutyDate: string;
  shiftCode: string;
  shiftName: string;
  shortLabel?: string | null;
  colorToken?: string | null;
  startsAtUtc: string;
  endsAtUtc: string;
  dutyTypeCode: string;
  status: AssignmentStatus;
  onCallTier?: OnCallTier | null;
}

export interface WorkflowFinding {
  employeeId: string;
  employeeName: string;
  ruleCode: string;
  severity: ConflictSeverity;
  message: string;
  blocking: boolean;
}

export interface DutyCandidate {
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  designation?: string | null;
  staffCategory?: string | null;
  /** True when taking the duty would break a critical rule. */
  blocked: boolean;
  issues: string[];
}

export interface SwapRequest {
  id: string;
  kind: SwapKind;
  status: SwapStatus;
  requesterEmployeeId: string;
  requesterName: string;
  requesterDuty?: WorkflowDuty | null;
  peerEmployeeId: string;
  peerName: string;
  peerDuty?: WorkflowDuty | null;
  reason: string;
  requestedAt: string;
  requestedByName?: string | null;
  peerRespondedAt?: string | null;
  peerComments?: string | null;
  decidedAt?: string | null;
  decidedByName?: string | null;
  decisionComments?: string | null;
  overrideReason?: string | null;
  /** Resolved server-side, so the screen never re-derives who may do what. */
  allowedActions: SwapAction[];
}

export interface SwapWriteResult {
  swap: SwapRequest;
  findings: WorkflowFinding[];
}

export interface SubstitutionResult {
  assignmentId: string;
  saved: boolean;
  findings: WorkflowFinding[];
}

export interface Substitution {
  id: string;
  assignmentId: string;
  dutyDate: string;
  shiftCode: string;
  originalEmployeeId: string;
  originalName: string;
  substituteEmployeeId: string;
  substituteName: string;
  reason: string;
  overrideReason?: string | null;
  substitutedAt: string;
  substitutedByName?: string | null;
}

export interface RosterApproval {
  id: string;
  fromStatus: RosterStatus;
  toStatus: RosterStatus;
  actionedBy: string;
  actionedByName?: string | null;
  actionedAt: string;
  comments?: string | null;
  overrideReason?: string | null;
}

// ---- notifications ---------------------------------------------------------------------------

export interface NotificationRow {
  id: string;
  eventType: string;
  subject?: string | null;
  message: string;
  link?: string | null;
  queuedAt: string;
  readAt?: string | null;
}

export interface NotificationPreference {
  eventType: string;
  label: string;
  description: string;
  inApp: boolean;
  email: boolean;
  push: boolean;
}

export interface NotificationPreferences {
  emailAvailable: boolean;
  pushAvailable: boolean;
  events: NotificationPreference[];
}

// ---- helpers ---------------------------------------------------------------------------------

export const SWAP_STATUS_LABEL: Record<SwapStatus, string> = {
  Requested: "Waiting for colleague",
  AcceptedByPeer: "Waiting for approval",
  DeclinedByPeer: "Declined",
  Approved: "Approved",
  Rejected: "Rejected",
  Cancelled: "Withdrawn",
};

export function swapTone(status: SwapStatus) {
  switch (status) {
    case "Requested": return "info" as const;
    case "AcceptedByPeer": return "warning" as const;
    case "Approved": return "success" as const;
    case "DeclinedByPeer":
    case "Rejected": return "critical" as const;
    case "Cancelled": return "neutral" as const;
  }
}

/** "Tue 14 Oct · MOR (06:00–14:00)" — how a duty reads in a list. */
export function describeDuty(duty: WorkflowDuty): string {
  const day = new Date(`${duty.dutyDate}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short", day: "numeric", month: "short",
  });
  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} · ${duty.shiftCode} (${time(duty.startsAtUtc)}–${time(duty.endsAtUtc)})`;
}

/** Relative time for a notification list — "5 min ago" reads faster than a timestamp. */
export function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
